/* @flow */
// typecheck: ignore file
import React from 'react';
import BoxBody from 'components/BoxBody.jsx';
import BoxButton from 'components/BoxButton.jsx';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog.jsx';
import Zopim from 'lib/Zopim';
import Glyph from 'components/Glyph.jsx';
import Image from 'components/Image.jsx';
import LinkConstants from 'constants/LinkConstants';
import DefaultArticles from 'support/constants/DefaultArticles.json';
import FaqActions from 'actions/FaqActions';
import Text from 'components/Text.jsx';
import Link from 'components/Link.jsx';
import AsyncSelect from 'components/AsyncSelect.jsx';
import AsyncSelectItem from 'components/AsyncSelectItem.jsx';
import { connect } from 'react-redux';

import './styles/SupportDialog.scss';

type State = {
  searchString: ?string,
  articles: Object[],
};

type Props = {
  zopimStatus: 'online' | 'offline',
  onRequestHide?: () => any,
};

class SupportDialog extends React.Component {
  props: Props;
  state: State;

  handleResultSelected = (item: Object) => {
    if (!item.props || !item.props.url) {
      return;
    }

    const link = item.props.url;
    window.open(link, '_blank');
  }

  highlightMatches = (text: string, searchString: string) => {
    const lowerText = text.toLowerCase();
    const searchWords = searchString.split(' ').filter(word => word.length >= 3);

    const matches = [];

    searchWords.forEach(word => {
      const index = lowerText.indexOf(word.toLowerCase());
      if (index >= 0) {
        matches.push([index, word]);
      }
    });

    if (matches.length > 0) {
      matches.sort((a, b) => {
        if (a[0] < b[0]) {
          return -1;
        } else if (b[0] < a[0]) {
          return 1;
        } else {
          if (a[1].length > b[1].length) {
            return -1;
          } else {
            return 1;
          }
        }
      });

      const match = matches[0];

      const list = [
        (<span>{text.slice(0, match[0])}</span>),
        (<span className="highlighted">{match[1]}</span>),
      ];

      return list.concat(this.highlightMatches(text.slice(match[0] + match[1].length), searchString));
    }

    return [(<span>{text}</span>)];
  }

  openZopim = () => {
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }

    Zopim.show();
  }

  searchAnswers = (query: string, callback: Function) => {
    if (query.length === 0) {
      this.setState({ searchString: query });
      return;
    }

    this.setState({ searchString: query, articles: [] });
    callback(FaqActions.loadFaqs(query).then(faqs => {
      return faqs.slice(0, 4).map(article => this.renderSearchResult(article, query));
    }));
  }

  renderNoResults = () => {
    const body = (
      <div>
        <Link>Contact us</Link>
        {' '}
        with your question
      </div>
    );
    const resultTemplate = this.renderSearchResultTemplate(
      LinkConstants.CONTACT_FORM,
      'We don’t have any FAQs about this topic yet.',
      body,
      'SupportDialog-faqSearchResults-item--noResult'
    );

    return (
      <div className="SupportDialog-noResults">
        {resultTemplate}
      </div>
    );
  }

  renderDefaultArticles = () => {
    return DefaultArticles.map((article, idx: number) => {
      return this.renderSearchResultTemplate(
        article.url,
        article.title,
        article.body,
        '',
        idx
      );
    });
  }

  renderSearchResult = (article: Object, searchString: string) => {
    const e = document.createElement('html');
    e.innerHTML = article.getBody();
    let text = e.innerText || e.textContent;
    // trim off extra spaces/NBSPs from the text
    text = text.replace(/^[ \u00a0]*/, '');
    return this.renderSearchResultTemplate(
      article.getUrl(),
      this.highlightMatches(article.getTitle(), searchString),
      this.highlightMatches(text, searchString),
      '',
      article.id
    );
  }

  renderSearchResultTemplate = (url: string, name: any, body: any, className: string = '', id: number = -1) => {
    return (
      <AsyncSelectItem id={id} title="" url={url}>
        <Link href={url} target="_blank" className={`SupportDialog-faqSearchResults-item ${className}`}>
          <h3 className="SupportDialog-faqSearchResults-item-title">
            {name}
          </h3>
          <Text truncate className="SupportDialog-faqSearchResults-item-body">
            {body}
          </Text>
        </Link>
      </AsyncSelectItem>
    );
  }

  renderTitle = () => {
    return (
      <div>
        <Image className="SupportDialog-titleImage" src={require('assets/images/book-open.svg')} />
        <div>
          We’re here to help!
        </div>
      </div>
    );
  }

  render = () => {
    return (
      <Dialog
        eventScreenName="SupportDialog"
        title={this.renderTitle()}
        className="SupportDialog"
        onRequestHide={this.props.onRequestHide}
        size="small">
        <BoxBody>
          <AsyncSelect
            layerClassName="SupportDialog-faqSearch"
            searchCallback={this.searchAnswers}
            onSelect={this.handleResultSelected}
            initialItems={this.renderDefaultArticles()}
            noResults={this.renderNoResults()}
            placeholder="Search our FAQs using keywords like “attach”"
            clearOnInputChange
            inputDelay={500}
            noQuery=" "
          />
        </BoxBody>
        <div className="SupportDialog-actions">
          <Link
            href={LinkConstants.ZENDESK}
            target="_blank"
            color="thunder"
            className="SupportDialog-actions-link"
            eventElementName="SupportDialog-actions-link">
            <div>
              <Glyph name="lightbulb" size="xlarge" solo className="SupportDialog-actions-link-glyph" />
              <div>Frequently asked questions</div>
            </div>
          </Link>
          <Link
            href={LinkConstants.TEACHER_RESOURCES}
            target="_blank"
            color="thunder"
            className="SupportDialog-actions-link"
            eventElementName="SupportDialog-actions-link">
            <div>
              <Glyph name="apple" size="xlarge" solo className="SupportDialog-actions-link-glyph" />
              <div>Resources</div>
            </div>
          </Link>
          <Link
            href={LinkConstants.YOUTUBE_TUTORIAL_VIDEOS}
            target="_blank"
            color="thunder"
            className="SupportDialog-actions-link"
            eventElementName="SupportDialog-actions-link">
            <div>
              <Glyph name="play" size="xlarge" solo className="SupportDialog-actions-link-glyph" />
              <div>Tutorial videos</div>
            </div>
          </Link>
          <Link
            href={LinkConstants.CONTACT_FORM}
            target="_blank"
            color="thunder"
            className="SupportDialog-actions-link"
            eventElementName="SupportDialog-actions-link">
            <div>
              <Glyph name="email" size="xlarge" solo className="SupportDialog-actions-link-glyph" />
              <div>Contact Remind</div>
            </div>
          </Link>
        </div>
        {
          !(CurrentUser.isSignedIn() && CurrentUser.getUser().isUnderage()) && this.props.zopimStatus !== 'offline' &&
            <BoxButton className="SupportDialog-liveChat" onClick={this.openZopim}>
              <Glyph name="remind-icon" size="large" />
              Get live help from our team!
            </BoxButton>
        }
      </Dialog>
    );
  }
}

function mapStateToProps(state: Object) {
  return {
    zopimStatus: state.zopim.status,
  };
}

export { SupportDialog };
export default connect(mapStateToProps, undefined)(SupportDialog);



// WEBPACK FOOTER //
// ./src/support/components/SupportDialog.jsx